export type JdOrderOutcome = 'completed' | 'fulfilling' | 'locked' | 'cancelled'

export interface JdOrderMetrics {
  orderCount: number
  soldItemCount: number
  orderAmount: number
  payableAmount: number
  merchantReceivable: number
  freightAmount: number
}

export interface JdDailyOrderRecord extends JdOrderMetrics {
  date: string
  outcome: JdOrderOutcome
}

export interface JdDailySkuOrderRecord {
  date: string
  outcome: JdOrderOutcome
  jdSkuId: string
  merchantSku: string
  itemNumber: string
  productName: string
  soldItemCount: number
  listAmount: number
  orderCount: number
}

export interface JdOrderFacts {
  schemaVersion: string
  generatedAt: string
  meta: {
    platformId: 'jd'
    storeId: string
    storeName: string
    timezone: string
    queryStart: string
    queryEnd: string
    exportedAt: string
    sourceFileName: string
    sourceSha256: string
    sourceRows: number
    sourceOrders: number
    importedOrders: number
    provisionalReceivableOrders: number
    excludedAfterQueryEndOrders: number
    excludedUnpaidCancelledOrders: number
    statusCounts: Record<string, number>
    caliber: string
  }
  summary: Record<JdOrderOutcome, JdOrderMetrics>
  daily: JdDailyOrderRecord[]
  dailySku: JdDailySkuOrderRecord[]
}

export interface JdAfterSalesMetrics {
  applicationCount: number
  itemCount: number
  productAmount: number
  successfulCount: number
  successfulItemCount: number
  successfulShippedCount: number
  successfulShippedItemCount: number
  successfulRefundAmount: number
  inProgressCount: number
  closedUnsuccessfulCount: number
  shippedCount: number
  unshippedCount: number
  unknownShipmentCount: number
}

export interface JdDailyAfterSalesRecord extends JdAfterSalesMetrics {
  date: string
  unattributedSuccessfulItemCount?: number
  unattributedSuccessfulShippedItemCount?: number
  linkedCompletedRefundAmount?: number
  linkedCompletedRefundCount?: number
  linkedCancelledRefundAmount?: number
  linkedCancelledRefundCount?: number
  linkedFulfillingRefundAmount?: number
  linkedFulfillingRefundCount?: number
  linkedOutsideRefundAmount?: number
  linkedOutsideRefundCount?: number
}

export interface JdDailySkuAfterSalesRecord extends JdAfterSalesMetrics {
  date: string
  jdSkuId: string
  productName: string
}

export interface JdAfterSalesFacts {
  schemaVersion: string
  generatedAt: string
  meta: {
    platformId: 'jd'
    storeId: string
    storeName: string
    queryStart: string
    queryEnd: string
    exportedAt: string
    sourceFileName: string
    sourceSha256: string
    totalRefundAllStatuses: number
    orderLinkage: {
      ordersSourceFileName: string
      ordersSourceSha256: string
      order_completed?: { count: number; refundAmount: number }
      order_cancelled?: { count: number; refundAmount: number }
      order_in_fulfillment?: { count: number; refundAmount: number }
      order_not_in_export?: { count: number; refundAmount: number }
    } | null
    caliber: string
  }
  summary: JdAfterSalesMetrics
  daily: JdDailyAfterSalesRecord[]
  dailySku: JdDailySkuAfterSalesRecord[]
}

const emptyOrderMetrics = (): JdOrderMetrics => ({
  orderCount: 0,
  soldItemCount: 0,
  orderAmount: 0,
  payableAmount: 0,
  merchantReceivable: 0,
  freightAmount: 0,
})

const addOrderMetrics = (target: JdOrderMetrics, row: JdOrderMetrics) => {
  target.orderCount += row.orderCount
  target.soldItemCount += row.soldItemCount
  target.orderAmount += row.orderAmount
  target.payableAmount += row.payableAmount
  target.merchantReceivable += row.merchantReceivable
  target.freightAmount += row.freightAmount
}

export interface JdOrderWindowMetrics {
  completed: JdOrderMetrics
  fulfilling: JdOrderMetrics
  locked: JdOrderMetrics
  cancelled: JdOrderMetrics
  /** 完成后服务单退款（按申请日，仅订单仍显示完成的退款） */
  completedOrderRefundAmount: number
  completedOrderRefundCount: number
  /** 跨期退款：退的是订单导出区间之前的订单 */
  outsideRefundAmount: number
  outsideRefundCount: number
  /** 净成交参考＝完成订单商家应收－完成后服务单退款；两条时间轴不同，标为参考值 */
  netCompletedReceivable: number
  linkageAvailable: boolean
  daily: Array<{
    date: string
    completedReceivable: number
    completedOrderCount: number
    fulfillingReceivable: number
    cancelledReceivable: number
    applicationCount: number
    successfulRefundAmount: number
    completedOrderRefundAmount: number | null
    outsideRefundAmount: number | null
    netCompletedReceivable: number | null
  }>
}

export function jdOrderWindowMetrics(
  orderFacts: JdOrderFacts,
  afterSalesFacts: JdAfterSalesFacts | null,
  startDate: string,
  endDate: string,
): JdOrderWindowMetrics {
  const inWindow = (date: string) => date >= startDate && date <= endDate
  const totals: Record<JdOrderOutcome, JdOrderMetrics> = {
    completed: emptyOrderMetrics(),
    fulfilling: emptyOrderMetrics(),
    locked: emptyOrderMetrics(),
    cancelled: emptyOrderMetrics(),
  }
  const orderByDate = new Map<string, Partial<Record<JdOrderOutcome, JdDailyOrderRecord>>>()
  for (const row of orderFacts.daily) {
    if (!inWindow(row.date)) continue
    addOrderMetrics(totals[row.outcome], row)
    const current = orderByDate.get(row.date) || {}
    current[row.outcome] = row
    orderByDate.set(row.date, current)
  }
  const afterSalesByDate = new Map<string, JdDailyAfterSalesRecord>()
  let linkageAvailable = Boolean(afterSalesFacts)
  for (const row of afterSalesFacts?.daily || []) {
    if (!inWindow(row.date)) continue
    afterSalesByDate.set(row.date, row)
    if (row.linkedCompletedRefundAmount == null) linkageAvailable = false
  }
  const dates = [...new Set([...orderByDate.keys(), ...afterSalesByDate.keys()])].sort()
  let completedOrderRefundAmount = 0
  let completedOrderRefundCount = 0
  let outsideRefundAmount = 0
  let outsideRefundCount = 0
  const daily = dates.map((date) => {
    const orders = orderByDate.get(date) || {}
    const afterSales = afterSalesByDate.get(date)
    const completedReceivable = orders.completed?.merchantReceivable || 0
    const linkedCompleted = linkageAvailable ? afterSales?.linkedCompletedRefundAmount ?? 0 : null
    const linkedOutside = linkageAvailable ? afterSales?.linkedOutsideRefundAmount ?? 0 : null
    if (linkageAvailable && afterSales) {
      completedOrderRefundAmount += afterSales.linkedCompletedRefundAmount ?? 0
      completedOrderRefundCount += afterSales.linkedCompletedRefundCount ?? 0
      outsideRefundAmount += afterSales.linkedOutsideRefundAmount ?? 0
      outsideRefundCount += afterSales.linkedOutsideRefundCount ?? 0
    }
    return {
      date,
      completedReceivable,
      completedOrderCount: orders.completed?.orderCount || 0,
      fulfillingReceivable: (orders.fulfilling?.merchantReceivable || 0) + (orders.locked?.merchantReceivable || 0),
      cancelledReceivable: orders.cancelled?.merchantReceivable || 0,
      applicationCount: afterSales?.applicationCount || 0,
      successfulRefundAmount: afterSales?.successfulRefundAmount || 0,
      completedOrderRefundAmount: linkedCompleted,
      outsideRefundAmount: linkedOutside,
      netCompletedReceivable: linkedCompleted == null ? null : completedReceivable - linkedCompleted,
    }
  })
  return {
    ...totals,
    completedOrderRefundAmount,
    completedOrderRefundCount,
    outsideRefundAmount,
    outsideRefundCount,
    netCompletedReceivable: totals.completed.merchantReceivable - completedOrderRefundAmount,
    linkageAvailable,
    daily,
  }
}
